/**
 * Sends the loom-onboarding `ui viewed` GASv3 event under `product: 'loom'`.
 *
 * This is the ONLY thing in WAC that may send events as `product: 'loom'`.
 *
 * Why a dedicated client? Every event created via `createAnalyticsEvent` is
 * forwarded by the root `WacAnalyticsListener` to the single shared client,
 * whose `product` ('marketing') is fixed at construction time. `product`
 * cannot be overridden per event, so the only supported way to submit as
 * `product: 'loom'` is a separate client constructed with `product: 'loom'`.
 *
 * Why not parameterise the shared `getAnalyticsWebClient`? Because that would
 * make it easy for any caller to send anything as `product: 'loom'`. Instead
 * this module owns a small, deliberately stripped-down copy of the client
 * setup, and guarantees that no other event is ever sent as loom:
 *
 * The only reason this event is sent as loom is to see in this experiment
 * isExperimentLoomOnboardingToWac if we change the MAU to start here, at
 * the beginning of onboarding to match control, will we see a rise in MAU?
 *
 * Why `startUIViewedEvent` and not a plain `sendUIEvent`? The SDK's
 * `startUIViewedEvent` is dwell-gated: it only actually fires once the tab
 * has held focus for 2 seconds (and throttles re-sends to once per
 * hour/user/tenant), which mirrors how `ui viewed` events are expected to
 * count towards MAU. This call site fires at the very start of onboarding,
 * well before the ~30s minimum provisioning time elapses, so there is no
 * risk of the redirect out-racing the 2s dwell timer.
 */

import AnalyticsWebClient, {
	envType,
	tenantType,
	userType,
} from '@atlassiansox/analytics-web-client';

import { getValidatedAtlassianAccountId } from '@/analytics/atl-analytics/atlassian-account-id';
import { Cookie } from '@/utils/cookie';
import { getLocalStorage } from '@/utils/localstorage';

const getLoomAnalyticsEnvironment = (): string => {
	switch (process.env['CLIENT_LIBRARY_ENV']) {
		case 'production':
			return envType.PROD;
		case 'staging':
			return envType.STAGING;
		case 'development':
			return envType.DEV;
		default:
			return envType.LOCAL;
	}
};

const createLoomClient = (): AnalyticsWebClient => {
	const client = new AnalyticsWebClient(
		{
			env: getLoomAnalyticsEnvironment(),
			product: 'loom',
			version: process.env['VERSION'] || '',
			locale: '',
		},
		{
			useStargate: ['production', 'staging'].includes(process.env['CLIENT_LIBRARY_ENV'] ?? ''),
			// Reuse the anonymous id the feature gates client was bootstrapped with in SSR,
			// rather than letting the analytics client generate a second one.
			customAnonymousIdGenerator: () =>
				window.featureGateBootstrapValues?.identifiers?.analyticsAnonymousId ?? '',
			// Default is 500; lowered so the event reliably flushes before any redirect.
			flushWaitInterval: 100,
		},
	);

	return client;
};

let cachedClient: AnalyticsWebClient | null = null;

// Module-private on purpose — see the header comment.
const getLoomClient = (): AnalyticsWebClient | null => {
	if (typeof window === 'undefined') {
		return null;
	}
	if (!cachedClient) {
		cachedClient = createLoomClient();
	}
	return cachedClient;
};

/**
 * The attributes `WacAnalyticsListener` adds to every regular event. This
 * event bypasses the listener, so they are added here, and always sent
 * (empty when unknown).
 */
const getListenerAttributes = () => ({
	loginUserDetected: Cookie.readCookie('login_user_detected') ?? 'false',
	// `WacAnalyticsListener` writes this key directly (no expiry key), so skip the expiry check.
	trace_id: getLocalStorage('trace_id', true) ?? '',
	cmsPageVariant: window.cmsPageVariant ?? '',
});

type SendLoomUiViewedEventArgs = {
	/**
	 * Best-effort tenant. Provisioning has only just started when this fires,
	 * so it is often unknown; when absent the event is still sent, just without
	 * a tenant.
	 */
	readonly cloudId?: string;
};

export const sendLoomUiViewedEvent = ({ cloudId }: SendLoomUiViewedEventArgs): void => {
	const client = getLoomClient();
	const aaid = getValidatedAtlassianAccountId();

	if (!client || !aaid) {
		return;
	}

	client.setUserInfo(userType.ATLASSIAN_ACCOUNT, aaid);

	// Since we've just started provisioning we probably don't have a cloud id, but
	// try to send one just in case
	if (cloudId) {
		client.setTenantInfo(tenantType.CLOUD_ID, cloudId);
	}

	client.setUIViewedAttributes({
		...getListenerAttributes(),
		// Empty, just copying cause loom sends these.
		loomUserId: '',
		loomOrgId: '',
		isWAConboarding: 'true',
	});
	// fires once the tab has had focus for 2s
	client.startUIViewedEvent(() => {
		client.stopUIViewedEvent();
	});
};
